import {
  buildTestIds,
  capitalizeFirstLetterAndLowercaseRest,
  lazyLoading,
  useAppContext,
} from "application";
import { Image } from "components";
import { ScrollToLink } from "infra";
import { useEffect, useRef, useState } from "react";
import { MediaLink, Price, Product } from "typing";
import { theme } from "ui";
import {
  BoxPriceProduct,
  ButtonAddProductToWeddingList,
} from "../../components";
import { ButtonCartProps } from "../../components/ButtonCart";
import { ButtonCartUnavailableProps } from "../../components/ButtonCartUnavailable";
import {
  BoxButtons,
  BoxImage,
  BoxInfo,
  BoxNav,
  BoxValueDescription,
  Description,
  Header,
  HeaderContent,
  SectionContainer,
} from "./styles";

interface Section extends Partial<Product> {
  description: string;
  idReference: string;
}
interface HeaderProductFixedProps extends Partial<Product> {
  packageCurrentState: Price | undefined;
  addItemCart: () => void;
  actionAddToGift: () => void;
  sectionIdItemsMenu: Section[];
  sendingItemToCart: boolean;
  activeButtonWeddingList?: boolean;
  maximumStockPerBranch?: number;
  isBlackTheme?: boolean;
}

const ButtonCart = lazyLoading<ButtonCartProps>(() =>
  import("../../components/ButtonCart").then((module) => module)
);

const ButtonCartUnavailable = lazyLoading<ButtonCartUnavailableProps>(() =>
  import("../../components/ButtonCartUnavailable").then((module) => module)
);

const HeaderProductFixed = ({
  mediaLinks,
  description,
  packageCurrentState,
  measuredUnity,
  sectionIdItemsMenu,
  tag,
  sendingItemToCart,
  addItemCart,
  actionAddToGift,
  maximumStockPerBranch,
  activeButtonWeddingList = false,
  isBlackTheme,
}: HeaderProductFixedProps) => {
  const { isClientMobile } = useAppContext();
  const containerRef = useRef<HTMLElement>(null);
  const [thumbImage, setImageThumb] = useState<MediaLink | undefined>();
  const [listItemsMenu, setItemsMenu] = useState<Section[] | []>([]);

  const mediaScaleBottom = isClientMobile ? 0.7 : 0.8;

  useEffect(() => {
    window.onscroll = () => {
      if (!containerRef.current) {
        return;
      }
      const top = window.pageYOffset || document.documentElement.scrollTop;
      if (top >= 250) {
        containerRef.current.style.display = "block";
        return;
      }
      containerRef.current.style.display = "none";
    };

    setImageThumb(
      mediaLinks?.find((item) =>
        item.isDefault ? item.isDefault : item.linkType === "THUMB"
      )
    );

    // wellington.braga Procurar posteriormente uma solução melhor para resolver o problema de não encontrar o elemento no DOM ao carregar a página
    setTimeout(() => {
      setItemsMenu(
        sectionIdItemsMenu.filter(({ idReference }) =>
          document.getElementById(idReference)
        )
      );
    }, 1000);
  }, [mediaLinks, sectionIdItemsMenu]);

  const hasUnavailableStockPerBranch =
    maximumStockPerBranch === 0 ||
    Number(maximumStockPerBranch) <
      1 * (packageCurrentState?.packingQuantity ?? 1);
  const hasUnavailableTag = tag?.isUnavailable;

  return (
    <Header
      {...buildTestIds("header-product-fixed")}
      isBlackTheme={isBlackTheme}
      ref={containerRef}
    >
      <HeaderContent {...buildTestIds("header-product-fixed-content")}>
        <BoxInfo {...buildTestIds("header-product-fixed-box-info")}>
          <SectionContainer
            {...buildTestIds("header-product-fixed-section-container")}
          >
            <BoxImage {...buildTestIds("header-product-fixed-box-image")}>
              <Image
                {...buildTestIds("header-product-fixed-image")}
                src={
                  thumbImage?.imageUrl ??
                  "https://fcx-s3-fercos-backoffice.ferreiracosta.com/thumb/default/default_thumb.svg"
                }
                alt="thumb do produto"
                height="100%"
                width="100%"
                layout="intrinsic"
              />
            </BoxImage>
            <Description
              {...buildTestIds("header-product-fixed-description")}
              isBlackTheme={isBlackTheme}
            >
              {description}
            </Description>
          </SectionContainer>
          <SectionContainer
            {...buildTestIds("header-product-fixed-section-container")}
            shouldAlignToRight
          >
            <BoxValueDescription
              {...buildTestIds("header-product-fixed-box-value-description")}
            >
              <BoxPriceProduct
                {...buildTestIds("header-product-fixed-box-price-product")}
                descriptionParcelsFontColor={theme.colors.neutral.white}
                isBlackFriday={isBlackTheme}
                flexDirectionTextDescount="column-reverse"
                packageCurrentState={packageCurrentState}
                installmentsIsVisibled={!isClientMobile}
                measuredUnity={measuredUnity}
                modalViewMore={false}
                marginBottomPrice={false}
                fontSizeDiscount={isClientMobile ? "0.9rem" : "1rem"}
                fontSizePrice={isClientMobile ? "1.5rem" : "2rem"}
                maxWidth="18rem"
                fontSizeDescriptionParcels="1.15rem"
                fontSizeDetailsValue={isClientMobile ? "1.1875rem" : "1.15rem"}
                notShouldShowOldPrice
                notShouldShowTagOff
                shouldShowTextSpotPriceOnHeaderFixedMobile={isClientMobile}
              />
            </BoxValueDescription>

            <BoxButtons
              {...buildTestIds("header-product-fixed-box-buttons")}
              activeButtonWeddingList={activeButtonWeddingList}
            >
              {hasUnavailableTag || hasUnavailableStockPerBranch ? (
                <ButtonCartUnavailable
                  {...buildTestIds(
                    "header-product-fixed-button-cart-unavailable"
                  )}
                  isHeaderProduct
                  buttonText={tag?.descriptionButton}
                  buttonBgColor={tag?.buttonColor}
                  unavailableDesc={tag?.descriptionReason}
                  mediaScaleBottom={mediaScaleBottom}
                  textVisibleMobile
                />
              ) : (
                <>
                  <ButtonCart
                    {...buildTestIds("header-product-fixed-button-cart")}
                    actionButton={addItemCart}
                    mediaScaleBottom={mediaScaleBottom}
                    textVisibleMobile
                    sendingItemToCart={sendingItemToCart}
                    sizeProps={!activeButtonWeddingList && isClientMobile}
                    buttonInTheFixedHeader={isClientMobile}
                  />
                  {activeButtonWeddingList ? (
                    <ButtonAddProductToWeddingList
                      {...buildTestIds(
                        "header-product-fixed-button-add-product-to-wedding-list"
                      )}
                      isBlackTheme={isBlackTheme}
                      actionAddToGift={actionAddToGift}
                      textVisibleMobile
                      borderLine
                      scaleMedia={mediaScaleBottom}
                    />
                  ) : null}
                </>
              )}
            </BoxButtons>
          </SectionContainer>
        </BoxInfo>
        <BoxNav
          {...buildTestIds("header-product-fixed-box-nav")}
          isBlackTheme={isBlackTheme}
        >
          {listItemsMenu?.map((item) => {
            return (
              <ScrollToLink
                key={item?.idReference}
                activeClass="active"
                offset={-130}
                spy
                to={item?.idReference}
              >
                {capitalizeFirstLetterAndLowercaseRest(item?.description)}
              </ScrollToLink>
            );
          })}
        </BoxNav>
      </HeaderContent>
    </Header>
  );
};

export default HeaderProductFixed;
